// src/contexts/AppContext.js
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  authAPI, usersAPI, clientsAPI, gpsAPI, trucksAPI, documentsAPI, geofencesAPI, travelHistoryAPI, settingsAPI, gpsLoadsAPI, savedLocationsAPI,
  getToken, setToken, removeToken,
} from '../services/api';
import { DEFAULT_MODULES, DEFAULT_SETTINGS } from '../data/store';

const AppContext = createContext(null);

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

// Haversine distance in meters between two lat/lng points
// Bulk travel-history upload tuning (see bulkAddTravelHistory)
const BULK_CHUNK_SIZE = 100;
const MAX_BULK_REQUESTS = 80; // hard ceiling per call, even while splitting
const BULK_RETRY_DELAYS_MS = [3000, 8000]; // waits before retry #1 and #2 when the server can't be reached
const BULK_PACING_MS = 300; // pause between batches so a sync doesn't hit the server in a tight burst

// Merges freshly-fetched travel history into what's already in state, by id,
// instead of REPLACING the whole list. Replacing was the bug: the server's
// combined list is capped at the newest 2000 rows across all trucks, so each
// refresh swapped out a truck's older entries (including ones just saved by a
// sync) for a list that no longer contained them — they showed up, then
// vanished on the next refresh. Merging only ever adds/updates, never drops.
function mergeTravelHistory(prev, incoming) {
  if (!incoming || incoming.length === 0) return prev;
  const byId = new Map(prev.map(h => [h.id, h]));
  let changed = false;
  for (const row of incoming) {
    if (!byId.has(row.id)) { byId.set(row.id, row); changed = true; }
  }
  if (!changed) return prev; // nothing new — keep the same array so nothing re-renders
  return Array.from(byId.values()).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
}

function distanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Accepts a polygon as either an array of {lat,lng} objects (how geofence
// points are stored/transmitted everywhere else in the app) or an array of
// [lat,lng] tuples, and normalizes to tuples. Prevents "not iterable"
// crashes if a point ever arrives as a plain object instead of a tuple.
function normalizePolygon(polygon) {
  return polygon.map(p => Array.isArray(p) ? p : [p.lat, p.lng]);
}

// Ray-casting point-in-polygon test. `polygon` is an array of [lat, lng]
// pairs or {lat,lng} objects — either is fine, see normalizePolygon above.
function pointInPolygon(lat, lng, polygon) {
  const poly = normalizePolygon(polygon);
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [latI, lngI] = poly[i];
    const [latJ, lngJ] = poly[j];
    const intersect = ((lngI > lng) !== (lngJ > lng)) &&
      (lat < (latJ - latI) * (lng - lngI) / (lngJ - lngI) + latI);
    if (intersect) inside = !inside;
  }
  return inside;
}

// Shortest distance in meters from a point to the nearest edge of a polygon
// (used to report "how far outside" in breach alerts). Treats lat/lng as flat
// coordinates scaled to meters locally — accurate enough at city scale.
function distanceToPolygonMeters(lat, lng, polygon) {
  const poly = normalizePolygon(polygon);
  const latToM = 111320;
  const lngToM = 111320 * Math.cos(lat * Math.PI / 180);
  const px = lng * lngToM, py = lat * latToM;

  let minDist = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const ax = poly[i][1] * lngToM, ay = poly[i][0] * latToM;
    const bx = poly[j][1] * lngToM, by = poly[j][0] * latToM;
    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const cx = ax + t * dx, cy = ay + t * dy;
    const dist = Math.sqrt((px - cx) ** 2 + (py - cy) ** 2);
    if (dist < minDist) minDist = dist;
  }
  return minDist;
}

export function AppProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // checking stored token on boot
  const [users,       setUsers]       = useState([]);
  const [clients,     setClients]     = useState([]);
  const [gpsDevices,  setGpsDevices]  = useState([]);
  const [trucks,      setTrucks]      = useState([]);
  const [documents,   setDocuments]   = useState([]);
  const [notification, setNotification] = useState(null);
  const [loading,     setLoading]     = useState(false);

  // ── Travel history + idle notifications ─────────────────────────────────────
  const [travelHistory, setTravelHistory] = useState([]); // loaded from the backend via loadAllData(), same as trucks/documents
  // Settings starts with sensible local defaults (so idle alerts still work
  // the instant the app loads) and is immediately overwritten by the real
  // shared value from the backend once loadAllData() runs — see below.
  // Shared across EVERY user this way, same as geofences/travel history,
  // instead of the old localStorage-per-browser behavior.
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [idleAlerts, setIdleAlerts] = useState([]); // active popup queue
  const lastIdleNotif = useRef({}); // truckId -> last alert timestamp (ms)

  // ── Geofencing ────────────────────────────────────────────────────────────
  const [geofences, setGeofences] = useState([]); // loaded from the backend via loadAllData(), same as trucks/documents
  const [gpsLoads, setGpsLoads] = useState([]); // SIM/mobile load records per GPS device
  const [savedLocations, setSavedLocations] = useState([]); // reusable named locations for encoding documents
  const [geofenceAlerts, setGeofenceAlerts] = useState([]); // active popup queue
  const geofenceBreachState = useRef({}); // geofenceId -> true if currently outside (for exit/re-entry transition detection)

  // Cross-component navigation: set by a notification's "View Truck" link,
  // consumed by App.js (switch to Live Tracking page) and LiveTracking.js
  // (select + pan to that truck), then cleared.
  const [focusTruckRequest, setFocusTruckRequest] = useState(null);

  // ── Notification helper ───────────────────────────────────────────────────
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type, id: Date.now() });
    setTimeout(() => setNotification(null), 3500);
  }, []);

  // ── Load all data ─────────────────────────────────────────────────────────
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c, g, t, d, gf, th, st, gl, sl] = await Promise.all([
        usersAPI.list().catch(() => []),
        clientsAPI.list().catch(() => []),
        gpsAPI.list().catch(() => []),
        trucksAPI.list().catch(() => []),
        documentsAPI.list().catch(() => []),
        geofencesAPI.list().catch(() => []),
        travelHistoryAPI.list().catch(() => []),
        settingsAPI.get().catch(() => DEFAULT_SETTINGS),
        gpsLoadsAPI.list().catch(() => []),
        savedLocationsAPI.list().catch(() => []),
      ]);
      setUsers(u);
      setClients(c);
      setGpsDevices(g);
      setTrucks(t);
      setDocuments(d);
      setGeofences(gf);
      setTravelHistory(th);
      setSettings(st);
      setGpsLoads(gl);
      setSavedLocations(sl);
    } catch (err) {
      console.error('Load data error:', err);
    }
    setLoading(false);
  }, []);

  // ── Boot: restore session from stored token ───────────────────────────────
  useEffect(() => {
    const token = getToken();
    if (token) {
      authAPI.me()
        .then(user => {
          setCurrentUser(user);
          loadAllData();
        })
        .catch(() => {
          removeToken();
        })
        .finally(() => setAuthLoading(false));
    } else {
      setAuthLoading(false);
    }
  }, []); // eslint-disable-line

  // ── Auth ──────────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    try {
      const { token, user } = await authAPI.login(email, password);
      setToken(token);
      setCurrentUser(user);
      await loadAllData();
      return { success: true, user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [loadAllData]);

  const logout = useCallback(() => {
    removeToken();
    setCurrentUser(null);
    setUsers([]); setClients([]); setGpsDevices([]); setTrucks([]); setDocuments([]);
  }, []);

  // ── Users ─────────────────────────────────────────────────────────────────
  const addUser = useCallback(async (data) => {
    try {
      const user = await usersAPI.create({
        ...data,
        modules: data.modules || DEFAULT_MODULES[data.role] || [],
      });
      setUsers(prev => [user, ...prev]);
      showNotification(`User "${user.name}" created`);
      return user;
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const updateUser = useCallback(async (id, data) => {
    try {
      const user = await usersAPI.update(id, data);
      setUsers(prev => prev.map(u => u.id === id ? user : u));
      showNotification('User updated');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const deleteUser = useCallback(async (id) => {
    try {
      await usersAPI.remove(id);
      setUsers(prev => prev.filter(u => u.id !== id));
      showNotification('User removed', 'info');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  // ── Clients ───────────────────────────────────────────────────────────────
  const addClient = useCallback(async (data) => {
    try {
      const client = await clientsAPI.create(data);
      setClients(prev => [client, ...prev]);
      showNotification(`Client "${client.name}" added`);
      return client;
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const updateClient = useCallback(async (id, data) => {
    try {
      const client = await clientsAPI.update(id, data);
      setClients(prev => prev.map(c => c.id === id ? client : c));
      showNotification('Client updated');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const deleteClient = useCallback(async (id) => {
    try {
      await clientsAPI.remove(id);
      setClients(prev => prev.filter(c => c.id !== id));
      showNotification('Client removed', 'info');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  // ── GPS Devices ───────────────────────────────────────────────────────────
  const addGPSDevice = useCallback(async (data) => {
    try {
      const device = await gpsAPI.create(data);
      setGpsDevices(prev => [device, ...prev]);
      showNotification(`GPS Device "${device.deviceId}" added`);
      return device;
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const updateGPSDevice = useCallback(async (id, data) => {
    try {
      const device = await gpsAPI.update(id, data);
      setGpsDevices(prev => prev.map(g => g.id === id ? device : g));
      showNotification('GPS Device updated');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const deleteGPSDevice = useCallback(async (id) => {
    try {
      await gpsAPI.remove(id);
      setGpsDevices(prev => prev.filter(g => g.id !== id));
      showNotification('GPS Device removed', 'info');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  // ── Trucks ────────────────────────────────────────────────────────────────
  const addTruck = useCallback(async (data) => {
    try {
      const truck = await trucksAPI.create(data);
      setTrucks(prev => [truck, ...prev]);
      if (data.gpsDeviceId) {
        setGpsDevices(prev => prev.map(g =>
          g.id === data.gpsDeviceId ? { ...g, status: 'assigned', assignedTruck: truck.id } : g
        ));
      }
      showNotification(`Truck "${truck.plateNumber}" added`);
      return truck;
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const updateTruck = useCallback(async (id, data) => {
    try {
      const truck = await trucksAPI.update(id, data);
      setTrucks(prev => prev.map(t => t.id === id ? truck : t));
      // Sync GPS device status locally
      if (data.gpsDeviceId !== undefined) {
        setGpsDevices(prev => prev.map(g => {
          if (g.assignedTruck === id && g.id !== data.gpsDeviceId)
            return { ...g, status: 'available', assignedTruck: null };
          if (g.id === data.gpsDeviceId)
            return { ...g, status: 'assigned', assignedTruck: id };
          return g;
        }));
      }
      showNotification('Truck updated');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const deleteTruck = useCallback(async (id) => {
    try {
      await trucksAPI.remove(id);
      setTrucks(prev => prev.filter(t => t.id !== id));
      setGpsDevices(prev => prev.map(g =>
        g.assignedTruck === id ? { ...g, status: 'available', assignedTruck: null } : g
      ));
      showNotification('Truck removed', 'info');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  // ── Documents ─────────────────────────────────────────────────────────────
  const addDocument = useCallback(async (data) => {
    try {
      const doc = await documentsAPI.create(data);
      setDocuments(prev => [doc, ...prev]);
      setTrucks(prev => prev.map(t =>
        t.id === doc.truckId ? { ...t, documents: [...(t.documents || []), doc.id] } : t
      ));
      showNotification(`Document "${doc.docNumber}" encoded`);
      return doc;
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const updateDocument = useCallback(async (id, data) => {
    try {
      const doc = await documentsAPI.update(id, data);
      setDocuments(prev => prev.map(d => d.id === id ? doc : d));
      showNotification('Document updated');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const deleteDocument = useCallback(async (id) => {
    try {
      await documentsAPI.remove(id);
      setDocuments(prev => prev.filter(d => d.id !== id));
      showNotification('Document deleted', 'info');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  // ── Settings ──────────────────────────────────────────────────────────────
  const updateSettings = useCallback(async (updates) => {
    try {
      const saved = await settingsAPI.update(updates);
      setSettings(saved);
      showNotification('Settings saved');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  // ── Travel history + idle detection ──────────────────────────────────────
  // Called by LiveTracking whenever a truck's live status transitions
  const recordStatusChange = useCallback((truck, newStatus, position) => {
    if (!truck) return;

    // Travel history recording itself now lives entirely on the backend
    // (see services/travelHistoryTracker.js) — it runs continuously there
    // regardless of whether anyone has this page open, which is far more
    // reliable than depending on a browser tab staying open for hours.
    // This function's only remaining job is the live idle-popup alert,
    // which is inherently a "someone is watching right now" feature and
    // stays here on the frontend.

    // Idle popup logic — fires immediately, no need to wait on anything.
    if (newStatus === 'idle' && settings.idleNotificationEnabled) {
      const last = lastIdleNotif.current[truck.id] || 0;
      const cooldownMs = (settings.idleNotificationRepeatMinutes || 10) * 60 * 1000;
      if (Date.now() - last > cooldownMs) {
        lastIdleNotif.current[truck.id] = Date.now();
        const alert = {
          id: uid(),
          truckId: truck.id,
          plateNumber: truck.plateNumber,
          driver: truck.driver,
          address: position.address || truck.location?.address || 'Unknown',
          timestamp: new Date().toISOString(),
          duration: settings.idlePopupDurationSeconds || 8,
        };
        setIdleAlerts(prev => [...prev, alert]);
        setTimeout(() => {
          setIdleAlerts(prev => prev.filter(a => a.id !== alert.id));
        }, (settings.idlePopupDurationSeconds || 8) * 1000);
      }
    }
  }, [settings]);

  const dismissIdleAlert = useCallback((alertId) => {
    setIdleAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  // ── Geofencing ────────────────────────────────────────────────────────────
  const addGeofence = useCallback(async (data) => {
    try {
      const gf = await geofencesAPI.create(data);
      setGeofences(prev => [gf, ...prev]);
      showNotification(`Geofence "${gf.name}" created`);
      return gf;
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const updateGeofence = useCallback(async (id, data) => {
    try {
      const gf = await geofencesAPI.update(id, data);
      setGeofences(prev => prev.map(g => g.id === id ? gf : g));
      showNotification('Geofence updated');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const deleteGeofence = useCallback(async (id) => {
    try {
      await geofencesAPI.remove(id);
      setGeofences(prev => prev.filter(g => g.id !== id));
      delete geofenceBreachState.current[id];
      showNotification('Geofence removed', 'info');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  // ── GPS SIM / Mobile Load Records ────────────────────────────────────────────
  const addGPSLoad = useCallback(async (data) => {
    try {
      const load = await gpsLoadsAPI.create(data);
      setGpsLoads(prev => [load, ...prev]);
      showNotification('Load record added');
      return load;
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const updateGPSLoad = useCallback(async (id, data) => {
    try {
      const load = await gpsLoadsAPI.update(id, data);
      setGpsLoads(prev => prev.map(l => l.id === id ? load : l));
      showNotification('Load record updated');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const deleteGPSLoad = useCallback(async (id) => {
    try {
      await gpsLoadsAPI.remove(id);
      setGpsLoads(prev => prev.filter(l => l.id !== id));
      showNotification('Load record removed', 'info');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  // ── Saved Locations ───────────────────────────────────────────────────────
  const addSavedLocation = useCallback(async (data) => {
    try {
      const loc = await savedLocationsAPI.create(data);
      setSavedLocations(prev => [...prev, loc].sort((a, b) => a.name.localeCompare(b.name)));
      showNotification(`"${loc.name}" saved for future use`);
      return loc;
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const deleteSavedLocation = useCallback(async (id) => {
    try {
      await savedLocationsAPI.remove(id);
      setSavedLocations(prev => prev.filter(l => l.id !== id));
      showNotification('Saved location removed', 'info');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  const dismissGeofenceAlert = useCallback((alertId) => {
    setGeofenceAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  // Called by LiveTracking on every position update for every truck that has
  // one or more active geofences assigned to it. Detects the OUTSIDE
  // transition (was inside/unknown → now outside) and fires a popup exactly
  // once per exit — re-arms automatically once the truck returns inside.
  const checkGeofenceViolations = useCallback((truck, position) => {
    if (!truck || !position?.lat) return;
    const truckGeofences = geofences.filter(g => g.truckId === truck.id && g.active);
    if (truckGeofences.length === 0) return;

    truckGeofences.forEach(gf => {
      let isOutside, distOutside;

      if (gf.shape === 'polygon' && gf.points?.length >= 3) {
        const inside = pointInPolygon(position.lat, position.lng, gf.points);
        isOutside = !inside;
        distOutside = isOutside ? Math.round(distanceToPolygonMeters(position.lat, position.lng, gf.points)) : 0;
      } else {
        const dist = distanceMeters(gf.centerLat, gf.centerLng, position.lat, position.lng);
        isOutside = dist > gf.radiusMeters;
        distOutside = Math.round(dist - gf.radiusMeters);
      }

      const wasOutside = geofenceBreachState.current[gf.id] || false;

      if (isOutside && !wasOutside) {
        // Just exited — fire a popup
        geofenceBreachState.current[gf.id] = true;
        const alert = {
          id: uid(),
          geofenceId: gf.id,
          geofenceName: gf.name,
          truckId: truck.id,
          plateNumber: truck.plateNumber,
          driver: truck.driver,
          address: position.address || truck.location?.address || 'Unknown',
          distanceOutside: distOutside,
          timestamp: new Date().toISOString(),
        };
        setGeofenceAlerts(prev => [...prev, alert]);
      } else if (!isOutside && wasOutside) {
        // Back inside — re-arm for next exit, no popup needed for re-entry
        geofenceBreachState.current[gf.id] = false;
      }
    });
  }, [geofences]);

  // "View Truck" on a notification calls this — App.js switches to the Live
  // Tracking page, then LiveTracking.js selects + pans to the truck and
  // clears the request.
  const navigateToTruck = useCallback((truckId) => {
    setFocusTruckRequest({ truckId, ts: Date.now() });
  }, []);

  const clearFocusTruckRequest = useCallback(() => {
    setFocusTruckRequest(null);
  }, []);

  const getHistoryForTruck = useCallback((truckId, dateFrom, dateTo) => {
    return travelHistory
      .filter(h => h.truckId === truckId
        && (!dateFrom || new Date(h.timestamp) >= new Date(dateFrom))
        && (!dateTo || new Date(h.timestamp) <= new Date(dateTo + 'T23:59:59')))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [travelHistory]);

  const clearHistoryForTruck = useCallback(async (truckId) => {
    try {
      await travelHistoryAPI.removeForTruck(truckId);
      setTravelHistory(prev => prev.filter(h => h.truckId !== truckId));
      showNotification('Travel history cleared', 'info');
    } catch (err) { showNotification(err.message, 'error'); }
  }, [showNotification]);

  // Lets the Travel History page pull in fresh entries on an interval while
  // it's open — history is only ever appended to by LiveTracking's polling,
  // which may be running in a different tab, a different user's session
  // entirely, or not at all right now, so the page can't rely on this
  // context's own state changing on its own the way it does when the
  // recorder and the viewer are the same mounted component.
  const refreshTravelHistory = useCallback(async () => {
    try {
      const th = await travelHistoryAPI.list();
      setTravelHistory(prev => mergeTravelHistory(prev, th));
    } catch (err) {
      console.error('Failed to refresh travel history:', err);
    }
  }, []);

  // Loads ONE truck's history from the server and merges it in. Unlike the
  // combined list, this isn't squeezed out by other trucks' rows, so a truck
  // with weeks of synced history gets all of it. Pass `limit` for a cheap
  // "just the newest rows" refresh. Returns the rows fetched for that truck.
  // THROWS on failure (rather than swallowing it) so callers that depend on
  // knowing what's already saved — like auto-sync deciding where to resume —
  // can stop instead of guessing and re-importing duplicates.
  const loadTravelHistoryForTruck = useCallback(async (truckId, limit) => {
    const rows = await travelHistoryAPI.listForTruck(truckId, limit);
    const forTruck = Array.isArray(rows) ? rows.filter(h => h.truckId === truckId) : [];
    setTravelHistory(prev => mergeTravelHistory(prev, Array.isArray(rows) ? rows : []));
    return forTruck;
  }, []);

  // Called when a new entry arrives live via the real-time stream (see
  // useTravelHistoryStream) — prepends it straight into state so the
  // Travel History page updates instantly, no polling wait needed.
  // Dedups by id in case the same entry also shows up from a manual
  // refresh around the same time.
  const addTravelHistoryEntryFromStream = useCallback((entry) => {
    setTravelHistory(prev => prev.some(h => h.id === entry.id) ? prev : [entry, ...prev]);
  }, []);

  // Used by the "Sync Travel History" button in Settings — inserts many
  // reconstructed entries in one request instead of one at a time.
  // Saves travel history entries in small batches instead of one giant
  // request. A truck with a big backlog (e.g. a first-ever sync pulling 30
  // days) used to go up as a single request — large enough to hit body-size
  // limits or run long enough to time out at the hosting proxy, which is why
  // only trucks with lots of history failed. Now:
  //  - entries go up in batches of BULK_CHUNK_SIZE, in order
  //  - if the server answers a batch with a server error (500) or "too
  //    large" (413), that batch is split in half and retried, narrowing down
  //    to the specific bad entries so ONLY those get skipped — everything
  //    else still saves
  //  - if the server can't be reached at all (down, restarting, overloaded —
  //    no real HTTP response) or answers with a gateway error (502/503/504),
  //    it stops right away instead of retrying, so it never piles more load
  //    onto a server that's already struggling
  // Returns { insertedCount, skippedCount }; throws only if it had to stop
  // early (with `insertedCount` attached so callers can report partial progress).

  const bulkAddTravelHistory = useCallback(async (entries) => {
    if (!entries.length) return { insertedCount: 0, skippedCount: 0 };

    const stats = { saved: [], insertedCount: 0, skippedCount: 0, requests: 0 };

    const uploadBatch = async (batch) => {
      for (let attempt = 0; ; attempt++) {
        if (stats.requests >= MAX_BULK_REQUESTS) {
          throw new Error('Too many retries while saving — stopped to avoid overloading the server.');
        }
        stats.requests++;
        try {
          const saved = await travelHistoryAPI.bulkCreate(batch);
          stats.saved.push(...(Array.isArray(saved) ? saved : []));
          stats.insertedCount += batch.length;
          return;
        } catch (err) {
          const status = err.status;

          // No real response (server down/restarting, hit a hosting limit) or a
          // gateway error: wait and try the SAME batch again a couple of times
          // before giving up. These are usually brief — a shared host briefly
          // refusing connections, or the Node app being recycled — so a short
          // pause often lets the rest of the save go through instead of losing
          // the whole truck after one hiccup. Waits are long enough not to add
          // load to a server that's already struggling.
          const unreachable = status == null || status === 502 || status === 503 || status === 504;
          if (unreachable) {
            if (attempt < BULK_RETRY_DELAYS_MS.length) {
              await new Promise(r => setTimeout(r, BULK_RETRY_DELAYS_MS[attempt]));
              continue;
            }
            throw err;
          }

          // Server answered with a real error for this batch: split it to isolate bad entries
          if (status === 500 || status === 413) {
            if (batch.length === 1) { stats.skippedCount += 1; return; } // isolated a single bad entry — skip just this one
            const mid = Math.ceil(batch.length / 2);
            await uploadBatch(batch.slice(0, mid));
            await uploadBatch(batch.slice(mid));
            return;
          }

          throw err; // 4xx that retrying/splitting can't fix (e.g. permissions)
        }
      }
    };

    try {
      for (let i = 0; i < entries.length; i += BULK_CHUNK_SIZE) {
        await uploadBatch(entries.slice(i, i + BULK_CHUNK_SIZE));
        if (i + BULK_CHUNK_SIZE < entries.length) await new Promise(r => setTimeout(r, BULK_PACING_MS)); // breathing room between batches
      }
    } catch (err) {
      err.insertedCount = stats.insertedCount;
      err.skippedCount = stats.skippedCount;
      throw err;
    } finally {
      // Merge whatever DID save into local state, even if we stopped partway
      if (stats.saved.length > 0) setTravelHistory(prev => mergeTravelHistory(prev, stats.saved));
    }

    return { insertedCount: stats.insertedCount, skippedCount: stats.skippedCount };
  }, []);


  const value = {
    currentUser, authLoading, loading,
    login, logout,
    users,      addUser,      updateUser,      deleteUser,
    clients,    addClient,    updateClient,    deleteClient,
    gpsDevices, addGPSDevice, updateGPSDevice, deleteGPSDevice,
    trucks,     addTruck,     updateTruck,     deleteTruck,
    documents,  addDocument,  updateDocument,  deleteDocument,
    notification, showNotification,
    reloadData: loadAllData,
    // Travel history + idle notifications
    travelHistory, getHistoryForTruck, clearHistoryForTruck, recordStatusChange, refreshTravelHistory, loadTravelHistoryForTruck, bulkAddTravelHistory, addTravelHistoryEntryFromStream,
    settings, updateSettings,
    idleAlerts, dismissIdleAlert,
    // Geofencing
    geofences, addGeofence, updateGeofence, deleteGeofence,
    gpsLoads, addGPSLoad, updateGPSLoad, deleteGPSLoad,
    savedLocations, addSavedLocation, deleteSavedLocation,
    geofenceAlerts, dismissGeofenceAlert, checkGeofenceViolations,
    // Cross-component "jump to this truck" navigation (used by notification links)
    focusTruckRequest, navigateToTruck, clearFocusTruckRequest,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
